import { APP_INITIALIZER, Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { Account } from '../../models/account.model';

import { AppInfo } from '../../models/app-info.model';
import { MenuItem } from '../../models/menu-item.model';
import { Session } from '../../models/session.model';
import { User } from '../../models/user.model';

import { Logger } from '../../services/logger/logger.service';
import { APP, HUB_URL, LOCATION } from '../../variables';

export function sessionServiceFactory(sessionService: SessionService) {
  return () => sessionService.load();
}

@Injectable()
export class SessionService {
  apiEndpoint: string;

  env: string;
  user: User;
  account: Account;
  apps: AppInfo[];
  menuItems: MenuItem[];
  currentApp: AppInfo;
  apiKeys: any;
  appConfig: any;
  isPublic: boolean;
  whitelabelInfo: any;
  publicId: string;

  constructor(
    @Inject(APP) private currentAppName: string,
    @Inject(HUB_URL) public hubUrl: string,
    @Inject(LOCATION) private location: any,
    private http: HttpClient,
    private logger: Logger,
  ) {
    const parts = location.hostname.split('.').slice(1);
    this.apiEndpoint = ['api', ...parts].join('.');
  }

  async load() {
    const params = {};
    const pathMatch = this.location.pathname.match(/^\/s\/([a-f0-9-]+)/);
    if (pathMatch) {
      this.publicId = pathMatch[1];
      params['public_id'] = pathMatch[1];
    }
    try {
      const session$ = this.http.get(`//${this.apiEndpoint}/v1/session`, { withCredentials: true, params });
      const session = await firstValueFrom(session$) as Session;
      if (!session.user) {
        throw new Error('Unable to get `session.user`');
      }

      if (!session.account) {
        throw new Error('Unable to get `session.account`');
      }

      if (!session.apps) {
        throw new Error('Unable to get `session.apps`');
      }

      if (!session.menuItems) {
        throw new Error('Unable to get `session.menuItems`');
      }

      this.env = session.env;
      this.user = new User(session.user);
      this.account = new Account(session.account);
      this.apps = this.getApps(session);
      this.menuItems = this.getMenuItems(session);
      this.currentApp = this.getCurrentApp();
      this.apiKeys = session.apiKeys;
      this.isPublic = session.isPublic;
      this.whitelabelInfo = session.whitelabelInfo;
      this.setFavicon();
    } catch (err) {
      if (err.status === 403) {
        this.location.href = `${this.hubUrl}/logout`;
      } else {
        this.logger.error(err);
        throw err;
      }
    }
    if (!pathMatch) {
      this.appConfig = await firstValueFrom(this.http.get(`//${this.apiEndpoint}/v1/config/app/${this.currentAppName}`, { withCredentials: true }));
    }
    return true;
  }

  public isBurtAdmin() {
    return this.user.role === 'burt_admin';
  }

  public getAppBaseUrl(app: string) {
    let host = location.hostname.split('.');
    host[0] = app;
    return host.join('.');
  }

  public isPlatformUS(app: string) {
    return location.hostname.includes('us.');
  }

  private getApps(session) {
    return (session.apps || []).map(app => new AppInfo(app));
  }

  private getMenuItems(session) {
    return (session.menuItems || []).map(item => new MenuItem(item));
  }

  private getCurrentApp() {
    if (this.currentAppName === 'home') {
      return new AppInfo({ app: 'home', title: 'Home' });
    }
    const currentApp = this.apps.find(app => app.app === this.currentAppName);

    if (!currentApp) {
      throw new Error('Unable to find `currentApp` in `session.apps`. Did you activate it under the admin section?\n ' +
        'Use `SessionServiceMock` if app is not defined in `session.apps` yet');
    }

    return currentApp;
  }

  private setFavicon() {
    const value = this.whitelabelInfo.favicon;
    if (value) {
      $('#favicon').attr('href', value);
    }
  }
}

export const SESSION_SERVICE_APP_INITIALIZER = {
  provide: APP_INITIALIZER,
  useFactory: sessionServiceFactory,
  deps: [SessionService],
  multi: true,
};
